import { useMemo, useState } from "react";
import {
    ArrowLeft,
    ArrowRight,
    CheckCircle2,
    MessageCircle,
    Sparkles,
    Wand2,
    Users,
    BriefcaseBusiness,
    Palette,
    Rocket
} from "lucide-react";

const AUDIENCE_OPTIONS = [
    "Local customers",
    "Parents / students",
    "Premium clients",
    "Home buyers",
    "Patients",
    "Business owners",
    "Online shoppers",
    "Startups / teams"
];

const INDUSTRY_OPTIONS = [
    "Education",
    "Real Estate",
    "Healthcare",
    "Ecommerce / Store",
    "Legal",
    "SaaS / Tech",
    "Restaurant",
    "Fitness",
    "Beauty",
    "Construction"
];

const OFFERING_OPTIONS_BY_INDUSTRY = {
    Education: [
        "Football training",
        "Weekend batches",
        "Beginner programs",
        "Advanced coaching",
        "Admissions enquiries"
    ],
    "Real Estate": [
        "Property listings",
        "Buying advisory",
        "Selling support",
        "Rental assistance",
        "Site visits"
    ],
    Healthcare: [
        "Doctor consultations",
        "Treatments",
        "Appointments",
        "Patient support",
        "Health checkups"
    ],
    "Ecommerce / Store": [
        "Products",
        "Collections",
        "Offers",
        "Online shopping",
        "Customer support"
    ],
    Legal: [
        "Consultations",
        "Practice areas",
        "Legal advisory",
        "Case evaluation",
        "Team profile"
    ],
    "SaaS / Tech": [
        "Product features",
        "Pricing",
        "Free trial",
        "Onboarding",
        "Team productivity"
    ],
    Restaurant: [
        "Menu",
        "Online orders",
        "Table booking",
        "Offers",
        "Location details"
    ],
    Fitness: [
        "Training programs",
        "Membership plans",
        "Personal coaching",
        "Class schedule",
        "Trial session"
    ],
    Beauty: [
        "Beauty services",
        "Appointments",
        "Packages",
        "Before-after gallery",
        "Customer reviews"
    ],
    Construction: [
        "Project portfolio",
        "Services",
        "Consultation",
        "Site enquiry",
        "Client trust"
    ]
};

const STYLE_OPTIONS = [
    {
        key: "Premium",
        label: "Premium",
        helper: "Trust-focused and polished"
    },
    {
        key: "Modern",
        label: "Modern",
        helper: "Clean startup-style layout"
    },
    {
        key: "Bold",
        label: "Bold",
        helper: "Strong visual energy"
    },
    {
        key: "Clean",
        label: "Clean",
        helper: "Simple and clear"
    },
    {
        key: "Luxury",
        label: "Luxury",
        helper: "Elegant and high-end"
    }
];

function valueOrDash(value) {
    return value && String(value).trim() ? value : "-";
}

function buildFinalDescription(form) {
    const existing = form.description?.trim();
    if (existing) return existing;

    const businessName = form.businessName || "This business";
    const industry = form.subIndustry || form.industry || "business";
    const audience = form.primaryAudience || "customers";
    const offerings = form.coreServices || "services";

    return `${businessName} is a ${industry} focused on serving ${audience}. Main offerings include ${offerings}. The website should feel trustworthy, clear, and conversion-ready.`;
}

function getOfferingOptions(industry) {
    return OFFERING_OPTIONS_BY_INDUSTRY[industry] || [
        "Services",
        "Consultation",
        "Lead capture",
        "Customer support",
        "Business profile"
    ];
}

function ChatBubble({ children, type = "ai" }) {
    return (
        <div className={`mgcf-bubble ${type}`}>
            {type === "ai" ? (
                <div className="mgcf-avatar">
                    <Sparkles size={15} />
                </div>
            ) : null}

            <div className="mgcf-bubble-card">
                {children}
            </div>
        </div>
    );
}

function OptionChips({ options, selected, onSelect }) {
    return (
        <div className="mgcf-chip-grid">
            {options.map((item) => {
                const option = typeof item === "string"
                    ? { key: item, label: item }
                    : item;

                const value = option.key || option.label;

                return (
                    <button
                        type="button"
                        key={value}
                        className={`mgcf-chip ${selected === value ? "active" : ""}`}
                        onClick={() => onSelect(value)}
                    >
                        <strong>{option.label}</strong>
                        {option.helper ? <span>{option.helper}</span> : null}
                    </button>
                );
            })}
        </div>
    );
}

export default function MobileGeneratorChatFlow({
    form,
    updateField,
    onBack,
    onGenerate,
    generating = false,
    canGenerate = true
}) {
    const [step, setStep] = useState(() => {
        if (!form.businessName) return 0;
        if (!form.primaryAudience) return 1;
        if (!form.coreServices) return 2;
        if (!form.brandStyle) return 3;
        return 4;
    });


    const [customValue, setCustomValue] = useState("");

    const offeringOptions = useMemo(() => {
        return getOfferingOptions(form.industry);
    }, [form.industry]);

    const pages = Array.isArray(form.pages) && form.pages.length > 0
        ? form.pages
        : ["Home", "About", "Services", "Contact"];

    const crmModules = useMemo(() => {
        const entity = String(form.primaryEntityType || "").toLowerCase();

        if (entity.includes("program")) return ["Programs", "Coaches", "Leads", "Testimonials"];
        if (entity.includes("listing")) return ["Listings", "Services", "Leads", "Testimonials"];
        if (entity.includes("product")) return ["Products", "Collections", "Leads", "Offers"];
        if (entity.includes("doctor")) return ["Doctors", "Services", "Appointments", "Leads"];
        if (entity.includes("feature")) return ["Features", "Pricing", "Leads", "FAQ"];

        return ["Services", "Leads", "Testimonials", "Settings"];
    }, [form.primaryEntityType]);

    const steps = [
        {
            key: "businessName",
            icon: BriefcaseBusiness,
            title: "What is your business name?",
            subtitle: "This will become your website brand and CRM project name.",
            value: form.businessName,
            placeholder: "e.g. Pilate Sports Academy",
            type: "input",
            apply: (value) => updateField("businessName", value)
        },
        {
            key: "industry",
            icon: BriefcaseBusiness,
            title: "What type of business is this?",
            subtitle: "This helps Nexus choose the right pages, CRM modules, and layout pattern.",
            value: form.industry,
            type: "chips",
            options: INDUSTRY_OPTIONS,
            apply: (value) => updateField("industry", value)
        },
        {
            key: "primaryAudience",
            icon: Users,
            title: "Who should this website speak to?",
            subtitle: "Pick the main audience so AI can shape the copy and CTA.",
            value: form.primaryAudience,
            type: "chips",
            options: AUDIENCE_OPTIONS,
            apply: (value) => updateField("primaryAudience", value)
        },
        {
            key: "coreServices",
            icon: Wand2,
            title: "What do you offer?",
            subtitle: "Choose common offerings or type your own.",
            value: form.coreServices,
            type: "chipsWithCustom",
            options: offeringOptions,
            apply: (value) => updateField("coreServices", value)
        },
        {
            key: "brandStyle",
            icon: Palette,
            title: "What style should the website feel like?",
            subtitle: "AI will combine this with your industry and goal.",
            value: form.brandStyle,
            type: "style",
            options: STYLE_OPTIONS,
            apply: (value) => {
                updateField("brandStyle", value);
                updateField("contentStyleHint", value.toLowerCase());
            }
        },
        {
            key: "summary",
            icon: CheckCircle2,
            title: "Your website plan is ready",
            subtitle: "Review the plan and generate your AI website.",
            type: "summary"
        }
    ];

    const current = steps[step];
    const CurrentIcon = current.icon;
    const isSummary = current.type === "summary";
    const progress = Math.round(((step + 1) / steps.length) * 100);

    const canContinue = isSummary || Boolean(current.value && String(current.value).trim());

    const applyCustom = () => {
        const value = customValue.trim();
        if (!value) return;

        current.apply(value);
        setCustomValue("");
    };

    const continueStep = () => {
        if (step < steps.length - 1) {
            setStep((prev) => prev + 1);
        }
    };

    const goBack = () => {
        if (step > 0) {
            setStep((prev) => prev - 1);
            return;
        }

        if (onBack) onBack();
    };

    const handleGenerate = () => {
        updateField("description", buildFinalDescription(form));

        if (!form.goalOfWebsite) {
            updateField("goalOfWebsite", "Generate Leads");
        }

        if (!form.brandStyle) {
            updateField("brandStyle", "Premium");
        }

        if (!form.tone) {
            updateField("tone", "Professional");
        }

        if (!form.pages || !Array.isArray(form.pages) || form.pages.length === 0) {
            updateField("pages", ["Home", "About", "Services", "Contact"]);
        }

        if (onGenerate) {
            setTimeout(() => {
                onGenerate();
            }, 0);
        }
    };

    return (
        <main className="mgcf-page">
            <section className="mgcf-shell">
                <div className="mgcf-topbar">
                    <button
                        type="button"
                        className="mgcf-icon-btn"
                        onClick={goBack}
                        aria-label="Back"
                    >
                        <ArrowLeft size={18} />
                    </button>

                    <div className="mgcf-brand">
                        <div className="mgcf-brand-icon">
                            <MessageCircle size={17} />
                        </div>

                        <div>
                            <strong>Nexus AI Builder</strong>
                            <span>{progress}% complete</span>
                        </div>
                    </div>

                    <div className="mgcf-progress-pill">
                        {step + 1}/{steps.length}
                    </div>
                </div>

                <div className="mgcf-progress">
                    <span style={{ width: `${progress}%` }} />
                </div>

                <div className="mgcf-thread">
                    <ChatBubble>
                        <div className="mgcf-question-head">
                            <div className="mgcf-question-icon">
                                <CurrentIcon size={18} />
                            </div>

                            <div>
                                <h1>{current.title}</h1>
                                <p>{current.subtitle}</p>
                            </div>
                        </div>
                    </ChatBubble>

                    {current.value && !isSummary ? (
                        <ChatBubble type="user">
                            <strong>{current.value}</strong>
                        </ChatBubble>
                    ) : null}

                    {current.type === "input" ? (
                        <div className="mgcf-answer-card">
                            <input
                                value={customValue || current.value || ""}
                                onChange={(e) => {
                                    setCustomValue(e.target.value);
                                    current.apply(e.target.value);
                                }}
                                placeholder={current.placeholder}
                                autoFocus
                            />
                        </div>
                    ) : null}

                    {current.type === "chips" ? (
                        <div className="mgcf-answer-card">
                            <OptionChips
                                options={current.options}
                                selected={current.value}
                                onSelect={(value) => current.apply(value)}
                            />
                        </div>
                    ) : null}

                    {current.type === "chipsWithCustom" ? (
                        <div className="mgcf-answer-card">
                            <OptionChips
                                options={current.options}
                                selected={current.value}
                                onSelect={(value) => {
                                    const existing = form.coreServices || "";
                                    const next = existing
                                        ? `${existing}, ${value}`
                                        : value;

                                    current.apply(next);
                                }}
                            />

                            <div className="mgcf-custom-row">
                                <input
                                    value={customValue}
                                    onChange={(e) => setCustomValue(e.target.value)}
                                    placeholder="Add custom offering"
                                />
                                <button type="button" onClick={applyCustom}>
                                    Add
                                </button>
                            </div>
                        </div>
                    ) : null}

                    {current.type === "style" ? (
                        <div className="mgcf-answer-card">
                            <OptionChips
                                options={current.options}
                                selected={current.value}
                                onSelect={(value) => current.apply(value)}
                            />
                        </div>
                    ) : null}

                    {isSummary ? (
                        <div className="mgcf-summary-card">
                            <div className="mgcf-summary-head">
                                <div>
                                    <span>Final Plan</span>
                                    <h2>{valueOrDash(form.businessName)}</h2>
                                    <p>
                                        Nexus will generate your website, CRM modules,
                                        lead capture flow, and preview.
                                    </p>
                                </div>

                                <div className="mgcf-summary-icon">
                                    <Rocket size={22} />
                                </div>
                            </div>

                            <div className="mgcf-summary-grid">
                                <div>
                                    <span>Industry</span>
                                    <strong>{valueOrDash(form.subIndustry || form.industry)}</strong>
                                </div>

                                <div>
                                    <span>Audience</span>
                                    <strong>{valueOrDash(form.primaryAudience)}</strong>
                                </div>

                                <div>
                                    <span>Offerings</span>
                                    <strong>{valueOrDash(form.coreServices)}</strong>
                                </div>

                                <div>
                                    <span>Style</span>
                                    <strong>{valueOrDash(form.brandStyle || "Premium")}</strong>
                                </div>
                            </div>

                            <div className="mgcf-plan-block">
                                <span>Pages</span>
                                <div>
                                    {pages.map((page) => (
                                        <em key={page}>{page}</em>
                                    ))}
                                </div>
                            </div>

                            <div className="mgcf-plan-block">
                                <span>CRM Modules</span>
                                <div>
                                    {crmModules.map((module) => (
                                        <em key={module}>{module}</em>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>

                <div className="mgcf-bottom-bar">
                    <button
                        type="button"
                        className="mgcf-secondary-btn"
                        onClick={isSummary ? () => setStep(0) : continueStep}
                    >
                        {isSummary ? "Edit Plan" : "Skip"}
                    </button>

                    {!isSummary ? (
                        <button
                            type="button"
                            className="mgcf-primary-btn"
                            disabled={!canContinue}
                            onClick={continueStep}
                        >
                            Continue
                            <ArrowRight size={17} />
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="mgcf-primary-btn mgcf-generate-btn"
                            disabled={generating || !canGenerate}
                            onClick={handleGenerate}
                        >
                            {generating ? "Generating..." : "Generate Website"}
                            <Rocket size={17} />
                        </button>
                    )}
                </div>
            </section>
        </main>
    );
}